import createClient from 'openapi-fetch';
import { ApiError } from './errors';
import { createContextMiddleware } from './middleware/context';
import { retryDelayMs, shouldRetry } from './retry';

/**
 * The only refresh answers that end a session (bkfc-cms docs/routes/auth.md): `SESSION_INVALID`
 * (a session was presented and is dead) and, in cookie mode, `LOGIN_REQUIRED` (no cookie at all).
 * Everything else — 429, 5xx, a network error — leaves the session intact.
 */
const ENDS_SESSION = ['SESSION_INVALID', 'LOGIN_REQUIRED'];

/** One refresh at a time across every tab of this origin: they share one cookie jar. */
export const REFRESH_LOCK = 'bkfc-v1-auth-refresh';

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * The browser session on `/v1/auth/*` in cookie mode (`X-BKFC-Session: cookie`): the refresh token
 * lives in an HttpOnly cookie on the auth host, the access token only in this closure.
 *
 * @param {{baseUrl: string, platform: string, appVersion: string, fetch?: typeof fetch,
 *   locks?: {request: (name: string, cb: () => Promise<any>) => Promise<any>} | null}} config
 */
export function createSession(config) {
  const locks = 'locks' in config ? config.locks : globalThis.navigator?.locks;
  const auth = createClient({
    baseUrl: config.baseUrl,
    fetch: config.fetch,
    credentials: 'include',
    headers: { 'X-BKFC-Session': 'cookie' },
  });
  auth.use(createContextMiddleware(config));

  let accessToken = null;
  let inFlight = null;
  // The refresh error that last ended the session; null after any sign-in or deliberate sign-out.
  let rejection = null;
  const listeners = new Set();

  const setToken = (token, endedBy = null) => {
    accessToken = token;
    rejection = endedBy;
    listeners.forEach(listener => listener());
  };

  async function post(path, body) {
    const { data, error, response } = await auth.POST(path, { body });
    if (!response.ok) {
      throw ApiError.fromResponseBody(error, response.status);
    }
    return data.data;
  }

  async function signIn(path, body) {
    const data = await post(path, body);
    setToken(data.access_token);
    return data;
  }

  async function refreshNow() {
    try {
      const data = await post('/v1/auth/refresh', {}); // empty body: the cookie is the credential
      setToken(data.access_token);
      return data.access_token;
    } catch (error) {
      if (error instanceof ApiError && ENDS_SESSION.includes(error.code)) {
        // Local sign-out only. Never call logout here: the server already refused the session,
        // and a stale 401 must not wipe a newer cookie another tab just set.
        setToken(null, error);
        return null;
      }
      throw error;
    }
  }

  /**
   * Single-flight: concurrent callers in this tab share one request, and the Web Lock serializes
   * tabs. Resolves the new access token, or null when the session ended; rejects on a transient
   * failure (session intact).
   */
  function refresh() {
    if (!inFlight) {
      // ponytail: no lock support -> in-tab single flight only; tabs race inside the server's 60s grace.
      const run = locks ? locks.request(REFRESH_LOCK, refreshNow) : refreshNow();
      inFlight = run.finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  }

  return {
    getAccessToken: () => accessToken,
    /** The ApiError (`SESSION_INVALID` / `LOGIN_REQUIRED`) the server ended the session with, else null. */
    lastRejection: () => rejection,
    /** For the bearer middleware: a transient failure keeps the original 401 instead of throwing. */
    refreshForRetry: () => refresh().catch(() => null),
    refresh,
    /** Boot: one refresh, retried with backoff only while the failure is transient. */
    async restore() {
      for (let attempt = 0; ; attempt += 1) {
        try {
          return await refresh();
        } catch (error) {
          if (!shouldRetry(attempt + 1, error)) {
            return null;
          }
          await sleep(retryDelayMs(attempt, error));
        }
      }
    },
    /** The bearer gate refused this session for good (`endedBy`: that ApiError). */
    endLocally: (endedBy = null) => setToken(null, endedBy),
    login: (email, password) => signIn('/v1/auth/login', { email, password }),
    register: body => signIn('/v1/auth/register', body),
    /** Clears local state whatever the server answers (auth.md: on 200, on 401, on no response). */
    async logout() {
      try {
        await post('/v1/auth/logout', {});
      } catch (_error) {
        // The server row is caught by the next gate rejection.
      } finally {
        setToken(null);
      }
    },
    forgot: email => post('/v1/auth/forgot', { email }),
    reset: body => post('/v1/auth/reset', body),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
